import { useState } from "react";
import type { UserConfidence } from "@cloudox/api-client";
import { ConfidenceBadge } from "../knowledge/ConfidenceBadge";
import { MarkdownContent } from "../markdown/MarkdownContent";

/**
 * One record an answer cited.
 *
 * Everything except the id is optional because two surfaces carry different
 * amounts of it: a live turn comes back with a label, a detail and the
 * record's own confidence, while a published demo fixture carries only the
 * ids the Agent emitted. Rendering a missing label as an empty row, or
 * borrowing the answer's confidence for a record that never stated one, would
 * both be inventions — so an absent field is simply not drawn.
 */
export interface AgentTurnCitation {
  readonly record_id: string;
  readonly label?: string;
  readonly detail?: string;
  readonly confidence?: UserConfidence;
}

/**
 * The shape this component renders.
 *
 * Deliberately structural rather than an import of one channel's DTO: the
 * live `ConversationTurn` satisfies it as-is, and a static fixture can be
 * adapted to it without either side depending on the other. Field names match
 * the API's so the live path needs no adapter at all — the risk being avoided
 * is a second renderer drifting from the first, and the cheapest way to avoid
 * it is for the live caller to pass its object straight through.
 */
export interface AgentTurnRecord {
  readonly question: string;
  readonly answer_markdown: string;
  readonly confidence: UserConfidence;
  readonly confidence_basis?: string;
  readonly assumptions?: readonly string[];
  readonly unknowns?: readonly string[];
  readonly gaps?: readonly string[];
  readonly citations?: readonly AgentTurnCitation[];
  readonly clarification_options?: readonly string[];
  readonly outcome?: string;
  readonly source?: string;
}

export interface AgentTurnProps {
  readonly turn: AgentTurnRecord;
  /** Ask a follow-up — used by the clarification choices. Omit where the
   *  surface cannot accept a new question, which also hides the choices. */
  readonly onAsk?: (question: string) => void;
  /** Render the reader's question above the answer. Off where the surface
   *  already shows it as a heading. */
  readonly showQuestion?: boolean;
}

/**
 * One question and its answer.
 *
 * The layout follows CloudoX's reporting contract rather than a chat
 * convention: the answer leads, its confidence sits beside it because that is
 * what tells a reader how far to trust it, and the evidence is one click away
 * rather than inlined — present, but not competing with the explanation.
 *
 * Everything shown here came from the turn. Nothing is recomputed: the
 * confidence label is the one the backend derived from the answer's primary
 * conclusion, and re-deriving it here (say, as the minimum over the citations)
 * would quietly contradict the server.
 *
 * Shared between the authenticated console and the public preview so that a
 * visitor evaluating the Agent is looking at the component the product ships,
 * not a demo-shaped imitation of it.
 */
export function AgentTurn({
  turn,
  onAsk,
  showQuestion = true,
}: AgentTurnProps) {
  const assumptions = turn.assumptions ?? [];
  const unknowns = turn.unknowns ?? [];
  const gaps = turn.gaps ?? [];
  const citations = turn.citations ?? [];
  const choices = turn.clarification_options ?? [];

  return (
    <div className="space-y-2">
      {showQuestion ? (
        <p
          className="rounded-lg px-3 py-2 text-sm font-medium"
          style={{ backgroundColor: "var(--bg-muted)", color: "var(--fg)" }}
        >
          {turn.question}
        </p>
      ) : null}

      <div className="space-y-2 px-1">
        <div
          className="text-sm assistant-answer"
          style={{ color: "var(--fg)" }}
        >
          {/* An answer is prose about a bounded package, not a document from
              the content tree: it has no diagrams and no links CloudoX wrote.
              So nothing here is fetched on render and no destination chosen
              inside an answer is one click away. */}
          <MarkdownContent
            markdown={turn.answer_markdown}
            enableImageZoom={false}
            imagePolicy="omit"
            externalLinkPolicy="reveal"
          />
        </div>

        {turn.outcome === "clarification" && choices.length > 0 && onAsk ? (
          <ClarificationChoices options={choices} onAsk={onAsk} />
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <ConfidenceBadge confidence={turn.confidence} size="md" />
          {turn.confidence_basis ? (
            <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
              {turn.confidence_basis}
            </span>
          ) : null}
        </div>

        <Notes title="Assumptions" items={assumptions} />
        <Notes title="Not known from this run" items={unknowns} />
        <Notes title="Evidence gaps" items={gaps} />

        {citations.length > 0 ? <Citations citations={citations} /> : null}

        {turn.source === "fallback" ? (
          <p
            className="flex items-start gap-1.5 text-xs"
            style={{ color: "var(--fg-muted)" }}
          >
            <InfoIcon />
            {/* Said plainly, because the answer is still complete and cited —
                only its wording is the deterministic rendering. Hiding this
                would misrepresent how the text was produced. */}
            {/* Said plainly, because the answer is still complete and cited —
                    only its wording is the deterministic rendering. Hiding this
                    would misrepresent how the text was produced. */}
            Written directly from the run's evidence (the AI summary was
            unavailable for this answer).
          </p>
        ) : null}
      </div>
    </div>
  );
}

function ClarificationChoices({
  options,
  onAsk,
}: {
  readonly options: readonly string[];
  readonly onAsk: (question: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className="btn btn-ghost text-xs"
          style={{ border: "1px solid var(--border)" }}
          onClick={() => onAsk(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function Notes({
  title,
  items,
}: {
  readonly title: string;
  readonly items: readonly string[];
}) {
  if (items.length === 0) return null;
  return (
    <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
      <span className="font-medium">{title}</span>
      <ul className="mt-0.5 space-y-0.5">
        {items.map((item) => (
          <li key={item}>&middot; {item}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The records the answer cites, collapsed by default.
 *
 * Each row keeps its **own** confidence: a Verified answer may rest partly on
 * an Assumed fact, and flattening that to the headline label is exactly the
 * kind of quiet overstatement the confidence model exists to prevent.
 */
function Citations({
  citations,
}: {
  readonly citations: readonly AgentTurnCitation[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-1 text-xs font-medium"
        style={{ color: "var(--fg-muted)" }}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <ChevronIcon open={open} />
        Evidence ({citations.length})
      </button>
      {open ? (
        <ul className="mt-1 space-y-1">
          {citations.map((citation) => (
            <li
              key={citation.record_id}
              className="rounded px-2 py-1 text-xs"
              style={{ backgroundColor: "var(--bg-muted)" }}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={citation.label ? "font-medium" : "font-mono"}
                  style={{ color: "var(--fg)", overflowWrap: "anywhere" }}
                >
                  {citation.label ?? citation.record_id}
                </span>
                {citation.confidence ? (
                  <ConfidenceBadge confidence={citation.confidence} size="sm" />
                ) : null}
              </div>
              {citation.detail ? (
                <p className="mt-0.5" style={{ color: "var(--fg-muted)" }}>
                  {citation.detail}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ChevronIcon({ open }: { readonly open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
    >
      <path d={open ? "m6 9 6 6 6-6" : "m9 18 6-6-6-6"} />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden
      style={{ flexShrink: 0, marginTop: "0.15rem" }}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}
